<template>
    <div class="col-span-12 lg:col-span-6">
        <div class="flex items-center justify-between mb-6">
            <page-header class="!mb-0">Calendly Schedules</page-header>
            <vx-button
                v-if="onCorporate() && canManageCalendly"
                @click="showModal = true"
                size="lg"
            >
                Assign Schedules
                <font-awesome-icon widthAuto :icon="faCirclePlus" />
            </vx-button>
        </div>

        <page-loading v-if="loading && !eventTypes.length"></page-loading>

        <template v-else-if="!loading && eventTypes.length === 0">
            <vx-box color="info" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                No Calendly schedules assigned yet.
                <vx-button
                    v-if="onCorporate() && canManageCalendly"
                    @click="showModal = true"
                    size="lg"
                >
                    Assign Schedules
                    <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </vx-box>
        </template>

        <template v-else>
            <vx-table v-if="eventTypes.length" class="xl:grid-cols-[2fr_auto_2fr] mt-0" rowBreakpoint="xl">
                <vx-table-row isHeader>
                    <div>Schedule Name</div>
                    <div>Duration</div>
                    <div>Scheduling URL</div>
                </vx-table-row>
                <vx-table-row v-for="eventType in eventTypes" :key="eventType.event_type_uri">
                    <vx-table-row-title>
                        <span class="break-all lg:line-clamp-2" :title="eventType.name">{{ eventType.name }}</span>
                    </vx-table-row-title>
                    <vx-table-row-item>
                        <template v-slot:label>Duration:</template>
                        <span class="whitespace-nowrap">{{ eventType.duration }} min</span>
                    </vx-table-row-item>
                    <vx-table-row-item>
                        <template v-slot:label>Scheduling URL:</template>
                        <a
                            :href="eventType.scheduling_url"
                            target="_blank"
                            class="text-sky-600 hover:text-sky-700 underline truncate"
                        >
                            {{ eventType.scheduling_url }}
                        </a>
                    </vx-table-row-item>
                </vx-table-row>
            </vx-table>
        </template>

        <group-calendly-modal-form
            v-model:visible="showModal"
            :groupId="props.groupId"
            :currentEventTypes="eventTypes"
            @saved="loadEventTypes"
        ></group-calendly-modal-form>
    </div>
</template>

<script setup lang="ts">
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ref, onMounted } from 'vue';
import { VxBox, VxButton, VxTable, VxTableRow, VxTableRowTitle, VxTableRowItem } from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import GroupCalendlyModalForm from './GroupCalendlyModalForm.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { calendlyClient } from '~/services';
import type { CalendlyEventType } from '~/services/calendly/calendlyClient.types';
import { catchRequestError } from '~/utils/request';

const teamId = Spark.state.currentTeam.id;
const { onCorporate } = useCurrentGroup();
const canManageCalendly = Spark.isAdminOrOwner;

const props = defineProps({
    groupId: {
        type: String,
        required: true,
    },
});

const eventTypes = ref<CalendlyEventType[]>([]);
const loading = ref<boolean>(true);
const showModal = ref<boolean>(false);

const loadEventTypes = async () => {
    loading.value = true;
    try {
        const response = await calendlyClient.getGroupMapping({ teamId }, props.groupId);
        eventTypes.value = response.data.event_types;
    } catch (error) {
        console.error('Failed to load Calendly event types:', error);
        catchRequestError(error);
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    loadEventTypes();
});
</script>
